import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import WebButton from '../../../component/library/webButton';
import { ProductSubscriptionApiService } from '../../../service/seller/productSubscriptionApiService';
import { useLoader } from '../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import ChooseSubscribedFile from '../../../component/views/seller/subscription/chooseSubscribedFile';
import Pagination from '../../../component/library/pagination';
import NoDataFound from '../../../component/common/noDataFound';
import { formatDate } from '../../../helper/utils';
import { useNavigate } from 'react-router-dom';
import DataExport from '../../../component/views/seller/subscription/dataExport';
import SyncProducts from '../../../component/views/seller/subscription/syncProducts';
import UnmappedProductsModal from './UnmappedProductsModal';
import './style.css';
import { SubscriptionApiService } from '../../../service/website/subscriptionApiService';
import AlibabaSubscriptionApiService from '../../../network/apiServices/AlibabaSubscriptionApiService';
import { IoChevronDownOutline } from 'react-icons/io5';
import FALLBACK_IMG from '../../../assets/images/common/importerr-no-image-logo.png';
import ProductSubscriptionSkeleton from './ProductSubscriptionSkeleton';

const initialTableState = {
  checkbox: 50,
  image: 100,
  sku: 130,
  name: 220,
  category: 200,
  // quantity: 100,
  sp: 100,
  // mrp: 140,
  shopify: 120,
  // shopifyDate: 160,
  amazon: 120,
  // amazonDate: 160,
  created: 190,
};

const columnsDef = [
  { key: 'checkbox', label: '' },
  { key: 'image', label: 'Image' },
  { key: 'sku', label: 'SKU' },
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  // { key: 'quantity', label: 'Quantity' },
  { key: 'sp', label: 'Selling Price' },
  // { key: 'mrp', label: 'MRP' },
  { key: 'shopify', label: 'Shopify Sync' },
  // { key: 'shopifyDate', label: 'Shopify Sync On' },
  { key: 'amazon', label: 'Amazon Sync' },
  // { key: 'amazonDate', label: 'Amazon Sync On' },
  { key: 'created', label: 'Subscribe On' },
];

// helper to normalize API rows into the shape you use
const normalizeProducts = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map(it => ({
    _id: it._id || it.id,
    title: it.title || it.name || '',
    imgUrl: Array.isArray(it.imgUrl) ? it.imgUrl : it.imgUrl ? [it.imgUrl] : [],
    c_name: it.c_name || it.category || it.categoryName || it.categoryPath || '',
    categoryPath: it.categoryPath || '',
    quantity: it.quantity || it.qty || 0,
    sku: it.sku || it.offerId || '',
    sp: it.sp || null,
    sp: it.sp || it.price || 0,
    mrp: it.mrp || it.price || 0,
    shopifySync: Boolean(it.shopifySync),
    shopifySyncDate: it.shopifySyncDate,
    amazonSync: Boolean(it.amazonSync),
    amazonSyncDate: it.amazonSyncDate,
    createdAt: it.createdAt || it.subscribeOn || it.created_at,
    c_id: it.c_id,
    categoryId: it.categoryId || it.c_id,
    source: it.source,
    location: it.location,
    variationsCount: it.variationsCount ?? (Array.isArray(it.variations) ? it.variations.length : 0), // Include variations count
    hasPricingIssue: Boolean(it.hasPricingIssue), // Flag for pricing issues (vw > wt*5)
  }));

const ProductSubscription = () => {
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();

  const [productData, setProductData] = useState([]); // current page from API
  const [allProductData, setAllProductData] = useState([]); // ALL products (for filters)
  const [unmappedCount, setUnmappedCount] = useState(0);
  const [selectedSKUs, setSelectedSKUs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showUnmappedModal, setShowUnmappedModal] = useState(false);

  // Amazon export — unmapped products are now handled by the backend (exported as "Unmapped Products.xlsx")

  // filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    shopify: 'all', // all | yes | no
    amazon: 'all', // all | yes | no
    search: '', // NEW: search text
  });

  // pagination
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({
    currentPage: '',
    totalPages: '',
    totalItems: '',
  });

  // table loading state for skeleton
  const [isTableLoading, setIsTableLoading] = useState(false);

  // column resize
  const tableWrapperRef = useRef(null); // wrapper div
  const tableRef = useRef(null); // <table className="seller_table">
  const activeColRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const [colWidths, setColWidths] = useState(initialTableState);

  // width we measure from the actual rendered table
  const [fullTableWidth, setFullTableWidth] = useState('100%');
  // expansion / variants cache
  const [expandedRows, setExpandedRows] = useState({});
  const [productVariants, setProductVariants] = useState({});
  
  // pricing issue modal state
  const [showPricingIssueModal, setShowPricingIssueModal] = useState(true);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 20 });
  const [modalSize, setModalSize] = useState({ width: 500, height: 'auto' });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const modalRef = useRef(null);

  const queryParams = `?limit=${limit}&page=${page}&includeDrafts=true`;

  // Note: Filters are now handled on backend, so we don't need isFilterActive for frontend filtering

  // measure table width so variant row can match it
  useEffect(() => {
    const measure = () => {
      if (tableRef.current) {
        const w = tableRef.current.getBoundingClientRect().width;
        setFullTableWidth(`${w}px`);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [colWidths, productData, expandedRows]);

  // ---------- Fetch subscription data ----------
  const fetchSubscriptionData = useCallback(
    async (options = {}) => {
      const { currentPage = page, pageLimit = limit } = options;

      try {
        setIsTableLoading(true);
        
        // Build query params with filters
        const queryParams = new URLSearchParams();
        queryParams.append('limit', pageLimit);
        queryParams.append('page', currentPage);
        queryParams.append('includeDrafts', 'true');
        
        // Add filter parameters
        if (filters.search && filters.search.trim()) {
          queryParams.append('search', filters.search.trim());
        }
        if (filters.startDate) {
          queryParams.append('fromDate', filters.startDate);
        }
        if (filters.endDate) {
          queryParams.append('toDate', filters.endDate);
        }
        if (filters.shopify !== 'all') {
          queryParams.append('shopifySync', filters.shopify);
        }
        if (filters.amazon !== 'all') {
          queryParams.append('amazonSync', filters.amazon);
        }
        
        const pageQuery = `?${queryParams.toString()}`;
        const res = await ProductSubscriptionApiService.getProductSubscription(pageQuery);

        if (res?.status?.code === 200) {
          const pageData = res.data;
          const normalized = normalizeProducts(pageData.products);

          setProductData(normalized);
          setPages({
            currentPage: pageData.currentPage || pageData.pagination?.currentPage,
            totalPages: pageData.totalPages || pageData.pagination?.totalPages,
            totalItems: pageData.totalProducts || pageData.pagination?.totalItems,
          });
          setPage(pageData.currentPage || pageData.pagination?.currentPage || 1);
          // No longer need allProductData since filtering is done on backend
          setAllProductData([]);

          // Calculate unmapped count from response
          const unmapped = normalized.filter(p => {
            const hasCategory = p?.categoryId && String(p.categoryId).trim();
            const hasCategoryName = p?.c_name && String(p.c_name).trim();
            const hasCategoryPath = p?.categoryPath && String(p.categoryPath).trim();
            const isMapped = hasCategory || hasCategoryName || hasCategoryPath;
            return !isMapped;
          }).length;
          setUnmappedCount(unmapped);
        } else {
          message.showToast(res?.error?.message || 'Failed to fetch products', ToastTypes.ERROR);
        }
      } catch (err) {
        console.error('Error fetching subscription data:', err);
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      } finally {
        setIsTableLoading(false);
      }
    },
    [page, limit, filters, message]
  );

  // Reset to page 1 when filters change (but not page/limit)
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    const filtersChanged = 
      prevFiltersRef.current.search !== filters.search ||
      prevFiltersRef.current.startDate !== filters.startDate ||
      prevFiltersRef.current.endDate !== filters.endDate ||
      prevFiltersRef.current.shopify !== filters.shopify ||
      prevFiltersRef.current.amazon !== filters.amazon;
    
    if (filtersChanged && page !== 1) {
      setPage(1);
    }
    prevFiltersRef.current = filters;
  }, [filters, page]);

  useEffect(() => {
    fetchSubscriptionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filters.search, filters.startDate, filters.endDate, filters.shopify, filters.amazon]);

  // Note: All filtering is now done on the backend, so we use productData directly
  // No need for filteredProductData or paginatedProductData - backend handles everything

  // helpers
  const findSelectedEntry = sku => selectedSKUs.find(item => item.sku === sku);
  const isSkuSelected = sku => !!findSelectedEntry(sku);

  // sync selectAll checkbox (based on what is currently visible)
  useEffect(() => {
    const baseList = productData;
    if (selectedSKUs.length) {
      setSelectAll(selectedSKUs.length === baseList.length && baseList.length > 0);
    } else {
      setSelectAll(false);
    }
  }, [selectedSKUs, productData]);

  const selectAllSubscription = e => {
    const baseList = productData;

    if (e.target.checked) {
      const mapped = baseList.map(item => ({
        sku: item.sku,
        isSynced: item.shopifySync,
        variantSkuIds: null, // null => all variants
      }));
      setSelectedSKUs(mapped);
    } else {
      setSelectedSKUs([]);
    }
  };

  const selectSubscription = (e, sku, isSynced) => {
    if (e.target.checked) {
      if (!isSkuSelected(sku)) {
        setSelectedSKUs(prev => [...prev, { sku, isSynced, variantSkuIds: null }]);
      }
    } else {
      setSelectedSKUs(prev => prev.filter(item => item.sku !== sku));
    }
  };

  const toggleVariantSelection = (parentSku, variantSkuId, checked) => {
    setSelectedSKUs(prev => {
      const next = [...prev];
      let idx = next.findIndex(x => x.sku === parentSku);

      if (idx === -1) {
        next.push({
          sku: parentSku,
          isSynced: false,
          variantSkuIds: checked ? [variantSkuId] : [],
        });
        return next;
      }

      const entry = { ...next[idx] };

      // if "all variants" before, convert to explicit list
      if (entry.variantSkuIds === null) {
        entry.variantSkuIds = [];
      }

      const setIds = new Set(entry.variantSkuIds || []);
      if (checked) setIds.add(variantSkuId);
      else setIds.delete(variantSkuId);

      entry.variantSkuIds = Array.from(setIds);
      next[idx] = entry;
      return next;
    });
  };

  const isVariantChecked = (parentSku, variantSkuId) => {
    const entry = findSelectedEntry(parentSku);
    if (!entry) return false;
    if (entry.variantSkuIds === null) return true; // parent means ALL
    return Array.isArray(entry.variantSkuIds) && entry.variantSkuIds.includes(variantSkuId);
  };

  const handleUnsubscribe = () => {
    if (!selectedSKUs.length) {
      message.showToast('Select at least one product', ToastTypes.ERROR);
      return;
    }

    loader.showLoading(true);

    const baseList = productData;

    const promises = selectedSKUs.map(({ sku }) => {
      const prod = baseList.find(p => p.sku === sku);
      const isAlibabaProduct = prod?.location === 'global' || prod?.source === 'alibaba' || prod?.location === 'alibaba';

      if (isAlibabaProduct) {
        return AlibabaSubscriptionApiService.unsubscribeFromProduct(sku);
      }
      return SubscriptionApiService.unSubscribeProduct(sku);
    });

    Promise.allSettled(promises)
      .then(results => {
        const success = results.filter(r => r.status === 'fulfilled' && r.value?.status?.code === 200).length;
        const failed = results.length - success;

        if (success > 0) {
          message.showToast(`Unsubscribed ${success} product(s) successfully`, ToastTypes.SUCCESS);
        }
        if (failed > 0) {
          message.showToast(`${failed} product(s) failed to unsubscribe`, ToastTypes.WARNING);
        }

        setSelectedSKUs([]);
        fetchSubscriptionData();
      })
      .catch(err => {
        console.error('Batch unsubscribe error:', err);
        message.showToast(err?.message || 'Server error. Try again later', ToastTypes.ERROR);
      })
      .finally(() => loader.showLoading(false));
  };

  const toggleExpandRow = async product => {
    const prodId = product._id;
    const alreadyExpanded = expandedRows[prodId];

    if (alreadyExpanded) {
      setExpandedRows(prev => ({ ...prev, [prodId]: false }));
      return;
    }

    setExpandedRows(prev => ({ ...prev, [prodId]: true }));

    if (!productVariants[prodId]?.data && !productVariants[prodId]?.loading) {
      setProductVariants(prev => ({
        ...prev,
        [prodId]: { loading: true, error: null, data: null, parentSku: product.sku },
      }));

      try {
        const res = await ProductSubscriptionApiService.getProductVariants(prodId, 'id');

        if (res.status.code === 200 && res.data && Array.isArray(res.data.variations)) {
          setProductVariants(prev => ({
            ...prev,
            [prodId]: {
              loading: false,
              error: null,
              data: res.data.variations,
              parentSku: res.data.parentSku || product.sku,
            },
          }));
        } else {
          setProductVariants(prev => ({
            ...prev,
            [prodId]: {
              loading: false,
              error: 'No variants found',
              data: [],
              parentSku: product.sku,
            },
          }));
        }
      } catch (err) {
        setProductVariants(prev => ({
          ...prev,
          [prodId]: {
            loading: false,
            error: 'Failed to load variants',
            data: [],
            parentSku: product.sku,
          },
        }));
      }
    }
  };

  // column resize handlers
  const startResize = (colKey, e) => {
    activeColRef.current = colKey;
    startXRef.current = e.clientX;
    startWidthRef.current = colWidths[colKey];

    if (tableWrapperRef.current) {
      tableWrapperRef.current.addEventListener('mousemove', handleMouseMove);
      tableWrapperRef.current.addEventListener('mouseup', handleMouseUp);
      tableWrapperRef.current.addEventListener('mouseleave', handleMouseUp);
    }
  };

  const handleMouseMove = e => {
    if (!activeColRef.current) return;
    const delta = e.clientX - startXRef.current;
    setColWidths(prev => ({
      ...prev,
      [activeColRef.current]: Math.max(50, startWidthRef.current + delta),
    }));
  };

  const handleMouseUp = () => {
    activeColRef.current = null;
    if (tableWrapperRef.current) {
      tableWrapperRef.current.removeEventListener('mousemove', handleMouseMove);
      tableWrapperRef.current.removeEventListener('mouseup', handleMouseUp);
      tableWrapperRef.current.removeEventListener('mouseleave', handleMouseUp);
    }
  };

  const measureNode = node => {
    if (!node) return 0;
    const clone = node.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.whiteSpace = 'nowrap';
    clone.style.width = 'auto';
    document.body.appendChild(clone);
    const width = clone.getBoundingClientRect().width;
    document.body.removeChild(clone);
    return Math.ceil(width);
  };

  const getDisplayPrice = row => {
    const d = Number(row?.sp);
    if (!Number.isNaN(d) && d > 0) return d;
    return null;
  };

  const handleDoubleClick = colKey => {
    if (!tableWrapperRef.current) return;
    let maxWidth = 50;

    const headerCell = tableRef.current.querySelector(`th[data-col='${colKey}']`);
    if (headerCell) {
      const clone = headerCell.cloneNode(true);
      const resizeHandle = clone.querySelector('.resize-handle');
      if (resizeHandle) resizeHandle.remove();
      maxWidth = Math.max(maxWidth, measureNode(clone));
    }

    const bodyCells = tableRef.current.querySelectorAll(`td[data-col='${colKey}']`);
    bodyCells.forEach(td => {
      maxWidth = Math.max(maxWidth, measureNode(td));
    });

    setColWidths(prev => ({ ...prev, [colKey]: maxWidth + 16 }));
  };

  const handleProduct = product => {
    // navigate(`/product-details?ali=1&offer=${product.sku}`);
    navigate(`/product-details?id=${product._id}`);
  };

  // Helper function to extract all unique attribute names from variants
  const getVariantAttributeNames = (variants) => {
    if (!Array.isArray(variants) || variants.length === 0) return [];
    
    const attributeSet = new Set();
    
    variants.forEach(v => {
      // Extract from attributes object (from new backend)
      if (v.attributes && typeof v.attributes === 'object') {
        Object.keys(v.attributes).forEach(key => {
          const value = v.attributes[key];
          if (value && String(value).trim()) {
            attributeSet.add(key);
          }
        });
      }
      // Also check for direct color/size (for backward compatibility with old backend)
      if (v.color && String(v.color).trim()) {
        attributeSet.add('Color');
      }
      if (v.size && String(v.size).trim()) {
        attributeSet.add('Size');
      }
    });
    
    // Convert to array and sort for consistent display
    return Array.from(attributeSet).sort((a, b) => {
      // Prioritize common attributes
      const priority = { 'Color': 1, 'Size': 2 };
      const aPriority = priority[a] || 999;
      const bPriority = priority[b] || 999;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return a.localeCompare(b);
    });
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      shopify: 'all',
      amazon: 'all',
      search: '',
    });
  };

  // pages for Pagination component - backend now handles all pagination
  const pagesForPagination = useMemo(() => {
    return pages;
  }, [pages]);

  // Get all products with pricing issues
  const productsWithPricingIssues = useMemo(() => {
    return productData.filter(p => p.hasPricingIssue);
  }, [productData]);

  // Drag handlers for pricing notice modal
  const handleDragStart = (e) => {
    e.preventDefault();
    if (modalRef.current) {
      const clientX = e.clientX || e.touches?.[0]?.clientX;
      const clientY = e.clientY || e.touches?.[0]?.clientY;
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: clientX - rect.left,
        y: clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  // Resize handlers for pricing notice modal
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      setResizeStart({
        x: e.clientX || e.touches?.[0]?.clientX,
        y: e.clientY || e.touches?.[0]?.clientY,
        width: rect.width,
        height: rect.height
      });
      setIsResizing(true);
    }
  };

  useEffect(() => {
    const handleMove = (e) => {
      const clientX = e.clientX || e.touches?.[0]?.clientX;
      const clientY = e.clientY || e.touches?.[0]?.clientY;
      
      if (isDragging && clientX && clientY) {
        const container = document.querySelector('.product_subscribe_bg');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const newX = clientX - containerRect.left - dragOffset.x;
          const newY = clientY - containerRect.top - dragOffset.y;
          
          // Constrain to container bounds
          const maxX = containerRect.width - (modalRef.current?.offsetWidth || 500);
          const maxY = containerRect.height - (modalRef.current?.offsetHeight || 200);
          
          setModalPosition({
            x: Math.max(0, Math.min(newX, maxX)),
            y: Math.max(0, Math.min(newY, maxY))
          });
        }
      } else if (isResizing && clientX && clientY) {
        const deltaX = clientX - resizeStart.x;
        const deltaY = clientY - resizeStart.y;
        
        const newWidth = Math.max(280, Math.min(800, resizeStart.width + deltaX));
        const newHeight = Math.max(150, resizeStart.height + deltaY);
        
        setModalSize({
          width: newWidth,
          height: newHeight
        });
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, isResizing, dragOffset, resizeStart]);

  return (
    <div className="product_subscribe_bg">
      {/* Pricing Issue Modal - Draggable Overlay */}
      {productsWithPricingIssues.length > 0 && showPricingIssueModal && (
        <div 
          className="pricing-issue-fixed-modal-overlay"
          style={{
            left: modalPosition.x || '50%',
            top: modalPosition.y || 20,
            transform: modalPosition.x ? 'none' : 'translateX(-50%)',
          }}
        >
          <div 
            ref={modalRef}
            className="pricing-issue-fixed-modal-content"
            style={{
              width: modalSize.width,
              height: modalSize.height === 'auto' ? 'auto' : `${modalSize.height}px`,
              minHeight: modalSize.height === 'auto' ? 'auto' : `${modalSize.height}px`,
            }}
          >
            <div 
              className="pricing-issue-fixed-modal-header"
              onMouseDown={handleDragStart}
              onTouchStart={handleDragStart}
              style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
              <div className="pricing-issue-fixed-modal-title">
                <span className="pricing-issue-icon">⚠️</span>
                Pricing Notice
              </div>
              <button
                type="button"
                className="pricing-issue-close-btn"
                onClick={() => setShowPricingIssueModal(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="pricing-issue-fixed-modal-body">
              <p className="pricing-issue-fixed-message">
                Pricing for these products may not be exact from the source. Please contact the admin to get better pricing.
              </p>
              <div className="pricing-issue-skus-list">
                <div className="pricing-issue-skus-title">Affected SKUs:</div>
                <div className="pricing-issue-skus-items">
                  {productsWithPricingIssues.map((product) => (
                    <span key={product._id} className="pricing-issue-sku-badge" title={product.title}>
                      {product.sku}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {/* Resize Handle */}
            <div 
              className="pricing-issue-resize-handle"
              onMouseDown={handleResizeStart}
              onTouchStart={handleResizeStart}
            />
          </div>
        </div>
      )}

      <div className="product_subscribe_actions_row">
        {/* left */}
        <div className="ps-left">
          {/* Filters */}
          <div className="ps-filters">
            {/* Row 1: Action Buttons */}
            <div className="ps-filters-row-1">
              <WebButton text={`Unmapped Category Products ${unmappedCount > 0 ? `(${unmappedCount})` : ''}`} onClick={() => setShowUnmappedModal(true)} className="unmapped_btn" />
              
              <div className="ps-filter-group">
                <label className="ps-filter-label">Shopify Sync</label>
                <select value={filters.shopify} onChange={e => setFilters(prev => ({ ...prev, shopify: e.target.value }))} className="ps-filter-select">
                  <option value="all">All</option>
                  <option value="yes">Synced</option>
                  <option value="no">Not Synced</option>
                </select>
              </div>

              <div className="ps-filter-group">
                <label className="ps-filter-label">Amazon Sync</label>
                <select value={filters.amazon} onChange={e => setFilters(prev => ({ ...prev, amazon: e.target.value }))} className="ps-filter-select">
                  <option value="all">All</option>
                  <option value="yes">Synced</option>
                  <option value="no">Not Synced</option>
                </select>
              </div>

              <DataExport selectedSKUs={selectedSKUs} setSelectedSKUs={setSelectedSKUs} setSelectAll={setSelectAll} />
              
              <SyncProducts selectedSKUs={selectedSKUs} setSelectedSKUs={setSelectedSKUs} setSelectAll={setSelectAll} />
              
              <WebButton text="Unsubscribe" onClick={handleUnsubscribe} className="active_btn_with_shadow" />
            </div>

            {/* Row 2: Search, Date Filter, Clear */}
            <div className="ps-filters-row-2">
              <div className="ps-filter-group">
                <label className="ps-filter-label">Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="ps-filter-input"
                  placeholder="Search SKU / Name / Category"
                />
              </div>

              <div className="ps-filter-group">
                <label className="ps-filter-label">Date</label>
                <div className="ps-filter-inline">
                  <input type="date" value={filters.startDate} onChange={e => setFilters(prev => ({ ...prev, startDate: e.target.value }))} className="ps-filter-input" />
                  <span className="ps-filter-separator">to</span>
                  <input type="date" value={filters.endDate} onChange={e => setFilters(prev => ({ ...prev, endDate: e.target.value }))} className="ps-filter-input" />
                </div>
              </div>

              <button type="button" className="ps-filters-clear-btn" onClick={handleClearFilters}>
                Clear all
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN TABLE */}
      <div className="seller_table_wrapper" ref={tableWrapperRef}>
        <div className="ps-limit-selector">
          <label className="ps-limit-label">Items per page:</label>
          <select
            value={limit}
            onChange={e => {
              const newLimit = Number(e.target.value);
              setLimit(newLimit);
              setPage(1);
            }}
            className="ps-limit-select"
          >
            <option value={10}>10</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
        <table className="seller_table" ref={tableRef}>
          <colgroup>
            {Object.keys(colWidths).map(key => (
              <col key={key} data-key={key} width={colWidths[key]} />
            ))}
          </colgroup>

          <thead>
            <tr>
              {columnsDef.map(col => (
                <th key={col.key} data-col={col.key}>
                  {col.key === 'checkbox' ? <input type="checkbox" checked={selectAll} onChange={selectAllSubscription} /> : col.label}

                  <div className="resize-handle" onMouseDown={e => startResize(col.key, e)} onDoubleClick={() => handleDoubleClick(col.key)} />
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {isTableLoading && <ProductSubscriptionSkeleton rows={limit} />}
            {!isTableLoading &&
              productData.map(item => {
                const isExpanded = expandedRows[item._id];
                const variantsInfo = productVariants[item._id] || {};
                const parentSelected = isSkuSelected(item.sku);

                const hasPricingIssue = item.hasPricingIssue;
                return (
                  <React.Fragment key={item._id}>
                    {/* parent row */}
                    <tr className={`product-row ${hasPricingIssue ? 'has-pricing-issue' : ''}`}>
                      <td data-col="checkbox">
                        <input type="checkbox" checked={parentSelected} onChange={e => selectSubscription(e, item.sku, item.shopifySync)} />
                      </td>

                      <td data-col="image">
                        <img src={item.imgUrl?.[0]} className="product_img" onClick={() => handleProduct(item)} alt="product" referrerPolicy="same-origin" />
                      </td>
                      <td data-col="sku">
                        <div className="sku-cell">
                          <div className="sku-text table_overflow_text" title={item?.sku}>
                            {item?.sku}
                          </div>

                          {(item?.variationsCount > 0) && (
                            <span
                              className={`variant-toggle-inline ${isExpanded ? 'expanded' : ''}`}
                              onClick={() => toggleExpandRow(item)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') toggleExpandRow(item);
                              }}
                              title={isExpanded ? 'Hide variants' : `Show variants (${item.variationsCount})`}
                            >
                              <span className="toggle-text" style={{ paddingLeft: '0.4rem' }}>
                                Show variants ({item.variationsCount})
                              </span>

                              <IoChevronDownOutline className="variant-arrow-icon" size={11} />
                            </span>
                          )}
                        </div>
                      </td>

                      <td data-col="name">
                        <div className="table_overflow_text cursor-pointer" onClick={() => handleProduct(item)} title={item?.title}>
                          {item?.title}
                        </div>
                      </td>

                      <td data-col="category">
                        <div className="table_overflow_text cursor-pointer" onClick={() => handleProduct(item)} title={item?.categoryPath}>
                          {item?.categoryPath}
                        </div>
                      </td>

                      <td data-col="sp">
                        {(() => {
                          const p = getDisplayPrice(item);
                          return p !== null ? `₹${p.toFixed(2)}` : '-';
                        })()}
                      </td>

                      <td data-col="shopify">
                        {(() => {
                          const vInfo = productVariants[item._id] || {};
                          const total = Array.isArray(vInfo.data) ? vInfo.data.length : 0;
                          const syncedCount = total ? vInfo.data.filter(v => v?.synced).length : 0;

                          if (total) {
                            if (syncedCount > 0) {
                              return (
                                <span className="text-success">
                                  Yes ({syncedCount} out of {total})
                                </span>
                              );
                            }
                            return <span className="text-muted">No</span>;
                          }

                          return <span className={item?.shopifySync ? 'text-success' : 'text-muted'}>{item?.shopifySync ? 'Yes' : 'No'}</span>;
                        })()}
                      </td>

                      <td data-col="amazon">
                        <span className={item?.amazonSync ? 'text-success' : 'text-muted'}>{item?.amazonSync ? 'Yes' : 'No'}</span>
                      </td>

                      <td data-col="created">{formatDate(item.createdAt)}</td>
                    </tr>

                    {/* variant row */}
                    {isExpanded && (
                      <tr className="variant-row">
                        <td
                          className="variant-cell"
                          colSpan={columnsDef.length}
                          style={{
                            width: fullTableWidth,
                            maxWidth: fullTableWidth,
                            padding: 0,
                          }}
                        >
                          {variantsInfo.loading ? (
                            <div className="variant-loading">Loading variants...</div>
                          ) : variantsInfo.error ? (
                            <div className="variant-error">{variantsInfo.error}</div>
                          ) : (() => {
                            const variants = variantsInfo.data || [];
                            const attributeNames = getVariantAttributeNames(variants);
                            const totalCols = 9 + attributeNames.length; // Select, Image, SKU, Attributes, Qty, Price, Weight, Shopify Sync, Shopify Sync On, Amazon Sync, Amazon Sync On

                            return (
                              <div
                                className="variant-table-wrapper"
                                style={{
                                  width: fullTableWidth,
                                  maxWidth: fullTableWidth,
                                  overflowX: 'auto',
                                }}
                              >
                                <table
                                  className="variant-table"
                                  style={{
                                    width: '100%',
                                    minWidth: fullTableWidth,
                                  }}
                                >
                                  <thead>
                                    <tr>
                                      <th style={{ width: '60px' }}>Select</th>
                                      <th style={{ width: '70px' }}>Image</th>
                                      <th>Variant SKU</th>
                                      {attributeNames.map(attrName => (
                                        <th key={attrName}>{attrName}</th>
                                      ))}
                                      <th>Qty</th>
                                      <th>Price</th>
                                      <th>Weight</th>
                                      <th>Shopify Sync</th>
                                      <th>Shopify Sync On</th>
                                      <th>Amazon Sync</th>
                                      <th>Amazon Sync On</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {variants.length === 0 ? (
                                      <tr>
                                        <td colSpan={totalCols} className="variant-empty">
                                          No variants found
                                        </td>
                                      </tr>
                                    ) : (
                                      variants.map(v => {
                                        const variantIdStr = String(v.skuId || v.sku);

                                        // Get attribute value helper
                                        const getAttributeValue = (attrName) => {
                                          // First check attributes object (new backend)
                                          if (v.attributes && typeof v.attributes === 'object') {
                                            // Try exact match first
                                            if (v.attributes[attrName]) {
                                              return v.attributes[attrName];
                                            }
                                            // Try case-insensitive match
                                            const lowerAttrName = attrName.toLowerCase();
                                            for (const key in v.attributes) {
                                              if (key.toLowerCase() === lowerAttrName) {
                                                return v.attributes[key];
                                              }
                                            }
                                          }
                                          // Fallback to direct properties for backward compatibility
                                          const lowerAttrName = attrName.toLowerCase();
                                          if (lowerAttrName === 'color' && v.color) return v.color;
                                          if (lowerAttrName === 'size' && v.size) return v.size;
                                          return null;
                                        };

                                        return (
                                          <tr key={v.skuId || v.sku}>
                                            <td>
                                              <input
                                                type="checkbox"
                                                checked={isVariantChecked(item.sku, variantIdStr)}
                                                onChange={e => toggleVariantSelection(item.sku, variantIdStr, e.target.checked)}
                                              />
                                            </td>

                                            <td>
                                              <img
                                                src={v.image || ''}
                                                alt="variant"
                                                style={{
                                                  width: '40px',
                                                  height: '40px',
                                                  borderRadius: '4px',
                                                  objectFit: 'cover',
                                                  border: '1px solid #e5e7eb',
                                                }}
                                                referrerPolicy="no-referrer"
                                                onError={ev => {
                                                  ev.currentTarget.onerror = null;
                                                  ev.currentTarget.src = FALLBACK_IMG;
                                                }}
                                              />
                                            </td>

                                            <td className="variant-sku-ellipsis" title={v.skuId}>
                                              {v.skuId || '-'}
                                            </td>

                                            {attributeNames.map(attrName => (
                                              <td key={attrName}>
                                                {getAttributeValue(attrName) || '-'}
                                              </td>
                                            ))}

                                            <td>{v.amountOnSale ?? '-'}</td>
                                            <td>{v.finalPrice !== undefined ? `₹${Number(v.finalPrice).toFixed(2)}` : '-'}</td>
                                            <td>{v.weight !== undefined ? `${v.weight}` : '-'}</td>
                                            <td>
                                              <span className={v.synced ? 'text-success' : 'text-muted'}>{v.synced ? 'Yes' : 'No'}</span>
                                            </td>
                                            <td>{v.syncedAt ? formatDate(v.syncedAt) : '-'}</td>

                                            <td>{'-'}</td>
                                            <td>{'-'}</td>
                                          </tr>
                                        );
                                      })
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* No data states */}
      {!isTableLoading && !productData.length && !allProductData.length && <NoDataFound message="No Subscribed Products Found" />}

      {!isTableLoading && productData.length === 0 && pages.totalItems === 0 && (
        <NoDataFound message="No products match applied filters" />
      )}

      {!isTableLoading && (productData.length > 0 || allProductData.length > 0) && <Pagination pages={pagesForPagination} page={page} setPage={setPage} />}

      <UnmappedProductsModal
        show={showUnmappedModal}
        onHide={() => setShowUnmappedModal(false)}
        onRefresh={fetchSubscriptionData}
        allProducts={productData}
        selectedSKUs={selectedSKUs}
      />

    </div>
  );
};

export default ProductSubscription;
